import type { Article } from "../../../types/blog";

const article: Article = {
  slug: "fiche-google-ou-site-internet",
  silo: "site-internet",
  h1: "Fiche Google Business Profile ou site internet : faut-il les deux ?",
  metaTitle: "Fiche Google ou site internet : faut-il les deux ?",
  metaDescription: "Fiche Google Business Profile ou site internet : quel rôle pour chacun, pourquoi ils se complètent et comment les relier pour un artisan ou un micro-entrepreneur local.",
  excerpt: "Fiche d'établissement Google ou site internet ? Pour un artisan ou un indépendant local, les deux outils ont des rôles différents et gagnent à être reliés.",
  publishedAt: "2026-10-09",
  readingMinutes: 6,
  coverImage: "/images/services/site-web.webp",
  coverImageAlt: "Fiche Google Business Profile et site internet pour artisan local",
  relatedSlugs: ["prix-site-internet-auto-entrepreneur", "wix-ou-professionnel-site-internet", "informatique-tpe-pme-support-indispensable"],
  faq: [
    {
      question: "Une fiche Google Business Profile remplace-t-elle un site internet ?",
      answer: "Non. La fiche donne les informations essentielles (adresse, horaires, avis, téléphone), mais elle ne permet pas de présenter en détail vos services, votre méthode ou vos réalisations comme le fait un site.",
    },
    {
      question: "Faut-il un site internet pour avoir une fiche Google ?",
      answer: "Non, une fiche peut exister sans site. Mais lui associer un site renforce la crédibilité et donne plus d'informations aux clients qui hésitent.",
    },
    {
      question: "Un micro-entrepreneur sans local peut-il avoir une fiche Google ?",
      answer: "Souvent oui, en tant qu'entreprise qui se déplace chez ses clients et qui indique ses zones d'intervention. Les règles évoluent : consultez toujours les conditions officielles de Google.",
    },
  ],
  content: [
    {
      type: "p",
      text: "Quand on lance une activité locale, deux outils reviennent sans cesse : la fiche Google Business Profile (anciennement Google My Business) et le site internet. Faut-il choisir l'un des deux, ou les deux ? Pour un auto-entrepreneur, un micro-entrepreneur, un artisan ou une TPE, la réponse tient dans la complémentarité de ces outils.",
    },
    {
      type: "h2",
      text: "Le rôle de la fiche Google Business Profile",
    },
    {
      type: "p",
      text: "La fiche est ce qui apparaît quand quelqu'un cherche votre activité ou un service près de chez lui : nom, catégorie, zone, horaires, téléphone, photos, avis clients. Elle est gratuite et se met en place rapidement.",
    },
    {
      type: "h3",
      text: "Ses points forts",
    },
    {
      type: "ul",
      items: [
        "Une présence directe dans les recherches locales et sur les cartes.",
        "Les avis clients, très consultés avant de contacter une entreprise.",
        "Un accès rapide à l'appel et à l'itinéraire depuis un smartphone.",
        "Un coût nul pour l'inscription.",
      ],
    },
    {
      type: "h3",
      text: "Ses limites",
    },
    {
      type: "p",
      text: "La fiche reste un format cadré : peu de place pour expliquer vos prestations, détailler vos tarifs ou raconter votre démarche. Son apparence dépend aussi des règles de Google, qui peuvent changer sans que vous ayez de contrôle.",
    },
    {
      type: "h2",
      text: "Le rôle du site internet",
    },
    {
      type: "p",
      text: "Le site est votre espace à vous. Vous y décidez de la présentation, des messages, des pages de services et de la façon de recueillir une demande de devis. Il rassure le client qui veut en savoir plus avant de vous contacter.",
    },
    {
      type: "ul",
      items: [
        "Une présentation complète de vos prestations et de votre manière de travailler.",
        "Des pages dédiées à chaque service ou zone d'intervention.",
        "Un formulaire de contact ou de demande de devis.",
        "Une adresse qui vous appartient, indépendante des plateformes.",
      ],
    },
    {
      type: "p",
      text: "Sa contrepartie : il demande un peu d'investissement et un suivi, comme nous le détaillons dans notre article sur le [prix d'un site internet pour un auto-entrepreneur](slug:prix-site-internet-auto-entrepreneur).",
    },
    {
      type: "h2",
      text: "Faut-il les deux ? Oui, dans la majorité des cas",
    },
    {
      type: "p",
      text: "La fiche Google attire l'attention et rassure grâce aux avis ; le site approfondit et convertit. Un client qui vous trouve sur Google clique souvent sur le site pour vérifier vos services avant d'appeler. Si le site n'existe pas ou semble dépassé, vous perdez une occasion de le convaincre.",
    },
    {
      type: "h3",
      text: "Le cas des artisans et indépendants locaux",
    },
    {
      type: "p",
      text: "Un plombier, un électricien, un coiffeur à domicile, un consultant ou un photographe sont cherchés par des habitants de leur secteur, par exemple en Haute-Savoie, dans le Pays de Gex ou autour de Genève. La fiche vous rend visible dans la zone ; le site explique ce que vous faites et pourquoi vous faire confiance.",
    },
    {
      type: "h3",
      text: "Si vous ne pouvez en faire qu'un seul",
    },
    {
      type: "p",
      text: "Pour démarrer avec un budget très limité, créer et soigner la fiche Google est généralement la première étape : elle est gratuite. Dès que votre activité se structure, ajouter un site vient naturellement compléter le dispositif.",
    },
    {
      type: "h2",
      text: "Ce que chaque outil apporte au client",
    },
    {
      type: "p",
      text: "Mettez-vous à la place de la personne qui cherche un professionnel près de chez elle. Elle tape un besoin sur son téléphone, voit plusieurs fiches, compare les avis, puis regarde quelques sites. Chaque outil répond à une étape différente de ce parcours.",
    },
    {
      type: "ul",
      items: [
        "La fiche Google répond à la question : « Qui est disponible près de moi, et que disent les clients ? »",
        "Le site répond à la question : « Que fait exactement cette personne, et puis-je lui faire confiance ? »",
        "Le formulaire ou le téléphone répond à la dernière étape : « Comment la contacter simplement ? »",
      ],
    },
    {
      type: "p",
      text: "Si un maillon manque, le client peut abandonner et se tourner vers un concurrent mieux présenté. C'est la raison pour laquelle on parle de complémentarité plutôt que de choix : l'un attire, l'autre convainc.",
    },
    {
      type: "h2",
      text: "Comment relier la fiche Google et le site",
    },
    {
      type: "ol",
      items: [
        "Renseignez l'adresse de votre site dans la fiche, pour que les clients puissent y accéder en un clic.",
        "Gardez des informations identiques partout : nom, téléphone, horaires, zone d'intervention.",
        "Reprenez sur le site les services listés dans la fiche, avec plus de détails.",
        "Ajoutez sur le site un lien ou un rappel vers vos avis Google, en respectant les règles de la plateforme.",
        "Mettez à jour les deux quand quelque chose change (horaires, tarifs, coordonnées).",
      ],
    },
    {
      type: "callout",
      variant: "tip",
      title: "Cohérence avant tout",
      text: "Des informations contradictoires entre la fiche et le site (un numéro différent, des horaires anciens) font perdre en crédibilité. Vérifiez-les régulièrement.",
    },
    {
      type: "h2",
      text: "Les erreurs fréquentes à éviter",
    },
    {
      type: "ul",
      items: [
        "Créer la fiche puis ne plus jamais la mettre à jour : horaires obsolètes, photos anciennes, avis sans réponse.",
        "Publier un site sans y indiquer clairement la zone d'intervention ni le moyen de vous contacter.",
        "Utiliser des noms d'activité différents entre la fiche, le site et vos factures.",
        "Négliger la lecture sur mobile, alors que la majorité des recherches locales se font depuis un smartphone.",
      ],
    },
    {
      type: "p",
      text: "Répondre poliment aux avis, y compris les plus critiques, montre le sérieux de votre activité et rassure les futurs clients. Ce travail demande peu de temps s'il est fait régulièrement.",
    },
    {
      type: "h2",
      text: "Un mot sur les mentions légales",
    },
    {
      type: "p",
      text: "Un site professionnel doit en principe afficher certaines informations sur l'éditeur (identité, coordonnées, hébergeur). Le détail dépend de votre statut et de votre activité : renseignez-vous auprès d'un interlocuteur compétent ou des sources officielles pour vous assurer d'être en règle.",
    },
    {
      type: "h2",
      text: "Faire le choix de la simplicité",
    },
    {
      type: "p",
      text: "Si vous ne souhaitez pas gérer la technique, un site clé en main peut être une bonne solution. Chez SOS IT Services, le site (jusqu'à 10 pages) est mis en ligne pour 350 €, puis 80 € par mois avec maintenance et modifications incluses, sur 12 mois d'engagement. L'hébergement et le nom de domaine sont à votre nom. Si vous hésitez encore entre faire seul et déléguer, lisez notre comparatif [Wix ou un professionnel](slug:wix-ou-professionnel-site-internet).",
    },
    {
      type: "p",
      text: "Pour les TPE qui s'équipent progressivement, notre article sur le [support informatique](slug:informatique-tpe-pme-support-indispensable) complète utilement cette réflexion.",
    },
    {
      type: "h2",
      text: "Conclusion",
    },
    {
      type: "p",
      text: "La fiche Google vous rend visible dans votre secteur, le site vous permet de convaincre et de présenter votre activité en détail. Pour un artisan, un auto-entrepreneur ou un micro-entrepreneur local, les deux ensemble sont plus efficaces que chacun séparément. Pour avoir un site simple et suivi, découvrez notre offre de [création de site internet pour auto-entrepreneur](page:/creation-site-web).",
    },
  ],
};

export default article;
